// DO NOT USE THIS FILE - USE /src/app/services/api.ts INSTEAD
// This file is kept for backwards compatibility but redirects to the shared service

import { getSharedSupabaseClient } from '../services/api';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';

const getClient = async () => {
  // Use the shared client getter from api.ts
  try {
    return await getSharedSupabaseClient();
  } catch (error) {
    console.error('Failed to get shared Supabase client:', error);
    return null;
  }
};

// Export lazy getter instead of direct client
export const supabase = {
  auth: {
    getSession: async () => {
      const client = await getClient();
      if (!client) return { data: { session: null }, error: new Error('Client not available') };
      return client.auth.getSession();
    },
    refreshSession: async () => {
      const client = await getClient();
      if (!client) return { data: { session: null }, error: new Error('Client not available') };
      return client.auth.refreshSession();
    },
    onAuthStateChange: async (callback: any) => {
      const client = await getClient();
      if (!client) return { data: { subscription: { unsubscribe: () => {} } } };
      return client.auth.onAuthStateChange(callback);
    },
    signOut: async () => {
      const client = await getClient();
      if (!client) return { error: new Error('Client not available') };
      return client.auth.signOut();
    },
    getUser: async (jwt?: string) => {
      const client = await getClient();
      if (!client) return { data: { user: null }, error: new Error('Client not available') };
      return client.auth.getUser(jwt);
    }
  }
};

// API base URL - cached
const apiUrlCache = `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75`;

export const getAPIURL = async () => {
  return apiUrlCache;
};

// Backwards compatibility - will be null initially
export const API_URL = apiUrlCache;

// Helper to get auth headers
export const getAuthHeaders = async () => {
  const client = await getClient();
  if (!client) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${publicAnonKey}`
    };
  }
  
  const { data: { session } } = await client.auth.getSession();
  
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session?.access_token || publicAnonKey}`
  };
};

// Helper for authenticated API calls
export const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const headers = await getAuthHeaders();
  const API_BASE = await getAPIURL();
  
  if (!API_BASE) {
    throw new Error('API not available');
  }
  
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `API call failed (${response.status}): ${response.statusText}`);
  }

  return response.json();
};